import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { RepositoryError } from '@application/ports';
import type { RuntimeConfig } from '@domain/entities/RuntimeConfig';
import type { FeatureKey, Permission, Role } from '@domain/vocabulary';
import { can } from '@domain/policies/RbacPolicy';
import { fallbackRuntimeConfig } from '@infrastructure/repositories/FirestoreRuntimeConfigRepository';
import { getServices } from '@infrastructure/services';

/**
 * Live runtime configuration.
 *
 * This provider is what makes the app OTA-safe in the sense the brief asked for:
 * feature flags, the permission matrix, the catalogues, the rules and the email
 * templates all arrive through a Firestore listener, so a superuser turning off a
 * module or granting a permission takes effect in every open session without a
 * redeploy and without a reload.
 *
 * It renders children immediately against the factory defaults rather than blocking
 * on the first snapshot. Two reasons: a cold load should not stare at a spinner
 * while six tiny documents arrive, and if the configuration is unreachable the user
 * should still get a working app rather than nothing. The defaults have every module
 * on, which is the right direction — an unreachable config document must not present
 * an application that appears to have lost half its features. The security floor is
 * unaffected, because it lives in the Firestore rules.
 */

interface RuntimeConfigState {
  readonly config: RuntimeConfig;
  /** False until the first snapshot lands; the UI can defer a flash of defaults. */
  readonly loaded: boolean;
  /** Set when the listener failed. The app keeps working on defaults. */
  readonly error: string | null;
  /** True when the deployed build is older than `minClientVersion`. */
  readonly needsReload: boolean;
}

const RuntimeConfigContext = createContext<RuntimeConfigState | null>(null);

/** How often to re-read the configuration when the live channel is unusable. */
const REST_POLL_MS = 60_000;

/**
 * Cuánto se le da al canal para traer algo del servidor antes de ir por REST.
 *
 * En una red sana el primer snapshot sale del caché y el del servidor llega
 * detrás en milisegundos, así que esperar evita pedir por REST —y ponerse a
 * sondear— en las 339 sesiones que no lo necesitan. Donde el canal está
 * bloqueado no llega nunca ninguno, y estos tres segundos son todo lo que la
 * pantalla pasa sobre valores de fábrica.
 */
const CACHE_GRACE_MS = 3_000;

export const RuntimeConfigProvider = ({ children }: { children: ReactNode }): ReactNode => {
  const [config, setConfig] = useState<RuntimeConfig>(fallbackRuntimeConfig);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Whether a real snapshot ever landed. See the banner rule below. */
  const everDelivered = useRef(false);
  /**
   * Quién está firmado, que es lo que decide si un rechazo significa algo.
   *
   * Este proveedor es el más externo —la configuración tiene que existir antes
   * que la sesión—, así que en un arranque sin sesión se suscribe **antes** de que
   * haya token. Las reglas piden `status: 'active'` en el token, de modo que esa
   * primera suscripción se rechaza siempre, y el rechazo llegaba como
   * `unauthenticated` → `permission-denied` → «No tienes acceso a la configuración
   * de la empresa»: un cartel de permisos por una sesión que todavía no existía.
   *
   * Y lo caro no era el cartel. Un `onSnapshot` rechazado **no se reintenta**: se
   * termina. Quien entraba desde la pantalla de acceso —sin recargar después— se
   * quedaba sin listener para toda la sesión y con la matriz de fábrica, que es la
   * avería que este archivo ya documenta más abajo. Reportado el 17 sep 2026 con
   * las tres líneas en consola y el cartel en pantalla.
   *
   * Por eso el efecto de abajo depende del usuario: al llegar la sesión se vuelve
   * a suscribir con el token bueno.
   */
  const [authUid, setAuthUid] = useState<string | null>(null);

  useEffect(() => {
    return getServices().auth.onAuthStateChanged((user) => {
      setAuthUid(user?.uid ?? null);
    });
  }, []);

  useEffect(() => {
    const services = getServices();
    /* Sesión nueva, nada ha llegado todavía: la cuenta empieza de cero y el cartel
       de la sesión anterior no sobrevive al cambio. */
    everDelivered.current = false;
    setError(null);
    let cancelled = false;
    let pollHandle: ReturnType<typeof setInterval> | undefined;
    let graceHandle: ReturnType<typeof setTimeout> | undefined;

    /*
     * The listener failing is not the same as the configuration being
     * unreachable, and treating them as one is what put a superuser in front of
     * «No tienes permiso para solicitar vacaciones».
     *
     * The listener rides Firestore's long-poll channel
     * (`/Listen/channel`, forced in `client.ts` because proxies break the
     * streaming one). A network that blocks *that* request while letting
     * ordinary REST calls through is not hypothetical — it is what one of ours
     * runs on, and the console says so: the channel dies on «access control
     * checks» while `documents:runAggregationQuery` reaches the server and
     * answers. On that machine the app fell back to the factory matrix, and the
     * factory matrix withholds `solicitar` from `admin` and `super` on purpose,
     * so the portal denied a permission the company's own configuration grants.
     *
     * So before giving up, ask once over REST. It costs one round trip on a path
     * that is already failing, it is the same documents the listener wanted, and
     * it turns "no live updates" — degraded but honest — into the failure it
     * actually is, instead of a silent policy change.
     */
    const fallBackToOneShot = async (): Promise<RepositoryError | null> => {
      const result = await services.config.load();
      if (cancelled) return null;
      // Returned rather than swallowed: the one-shot is the attempt that decided
      // the outcome, so its verdict — «no tienes acceso» rather than «no hay
      // red» — is the one worth putting in front of somebody.
      if (!result.ok) return result.error;

      everDelivered.current = true;
      setConfig(result.value);
      setLoaded(true);
      setError(null);
      services.logger.warn('runtime config read over REST; live updates are off', {});
      startPolling();
      return null;
    };

    /*
     * With the stream dead, the only way a change reaches an open session is to
     * ask again.
     *
     * OTA is the product's claim — turn a module off and it leaves every open
     * sidebar at once — and on a network that blocks the channel that claim
     * silently becomes "until you reload". A minute of staleness keeps the
     * promise nearly intact for the cost of two documents; it only ever runs on
     * the degraded path, so a healthy session pays nothing.
     */
    const startPolling = (): void => {
      if (pollHandle !== undefined) return;
      pollHandle = setInterval(() => {
        void services.config.load().then((result) => {
          if (!cancelled && result.ok) setConfig(result.value);
        });
      }, REST_POLL_MS);
    };

    /*
     * Un snapshot del caché no es la configuración de la empresa.
     *
     * Firestore entrega el estado local antes de hablar con el servidor, y en la
     * red que bloquea el canal ése es el único que llega. **Un caché vacío es un
     * snapshot de cero documentos**, y `assembleConfig` sobre cero documentos
     * devuelve los valores de fábrica sin que nada falle: ni error, ni banner, ni
     * entrada en el log. La sesión entera —matriz de permisos incluida— se queda
     * con lo que trae el binario.
     *
     * Medido en producción el 24 ago 2026, y por partida doble: el perfil decía
     * «prescribe a los 18 meses» cuando `config/rules` guarda 12, y el puesto de
     * una persona salía como `ios-tech-lead-developer` porque el catálogo que
     * había llegado tenía una entrada en vez de 320. Es la misma avería que dejó
     * a un superusuario sin permiso para pedir vacaciones, y la que el camino de
     * error de aquí abajo ya sabía resolver — sólo que nadie lo llamaba, porque
     * el canal no da error: se calla.
     */
    const vigilarCache = (): void => {
      if (graceHandle !== undefined || authUid === null) return;
      graceHandle = setTimeout(() => {
        if (cancelled || everDelivered.current) return;
        void fallBackToOneShot();
      }, CACHE_GRACE_MS);
    };

    const unsubscribe = services.config.subscribe(
      (next, fromCache) => {
        setConfig(next);
        setLoaded(true);
        setError(null);

        if (fromCache) {
          vigilarCache();
          return;
        }
        /* Del servidor: esto sí es la configuración de la empresa. */
        everDelivered.current = true;
        if (graceHandle !== undefined) clearTimeout(graceHandle);
        if (pollHandle !== undefined) {
          clearInterval(pollHandle);
          pollHandle = undefined;
        }
      },
      (repositoryError) => {
        services.logger.error('runtime config listener failed', {
          error: repositoryError,
          everDelivered: everDelivered.current,
          firmado: authUid !== null,
        });

        /*
         * Sin sesión no hay nada que leer, y desde luego no hay nada que anunciar.
         *
         * Las reglas rechazan a quien no trae token; eso no es «no tienes acceso»,
         * es «todavía no has entrado». Se espera a la sesión, que vuelve a montar
         * este efecto con el token bueno.
         */
        if (authUid === null) return;

        /*
         * The banner is only true when the screen really is on defaults.
         *
         * The listener serves the persisted cache before it reaches the server,
         * so a failure *after* the first snapshot leaves the app on the last
         * configuration it read — real values, merely frozen. Announcing "valores
         * por omisión" there contradicts every figure on screen, which is exactly
         * what the team saw. The failure still goes to the log, where it belongs.
         */
        if (everDelivered.current) {
          setLoaded(true);
          return;
        }

        // Nothing has landed yet, so the one-shot read decides whether this is a
        // degraded session or a broken one. Only when that fails too does the
        // screen go on defaults and say so.
        void fallBackToOneShot().then((oneShotError) => {
          if (oneShotError === null || cancelled) return;

          // Deliberately not clearing the config: the defaults keep the app usable.
          // The technical reason rides along, for the same reason it does in
          // Inicio: whoever sees this banner is not whoever can read a console.
          const reason = 'detail' in oneShotError ? oneShotError.detail : oneShotError.what;
          setError(
            oneShotError.kind === 'permission-denied'
              ? 'No tienes acceso a la configuración de la empresa.'
              : `No pudimos leer la configuración; el portal opera con valores por omisión. · ${reason}`,
          );
          setLoaded(true);
        });
      },
    );

    return () => {
      cancelled = true;
      if (pollHandle !== undefined) clearInterval(pollHandle);
      if (graceHandle !== undefined) clearTimeout(graceHandle);
      unsubscribe();
    };
  }, [authUid]);

  const value = useMemo<RuntimeConfigState>(
    () => ({
      config,
      loaded,
      error,
      needsReload: isOlderThan(__APP_VERSION__, config.minClientVersion),
    }),
    [config, loaded, error],
  );

  return <RuntimeConfigContext.Provider value={value}>{children}</RuntimeConfigContext.Provider>;
};

export const useRuntimeConfigState = (): RuntimeConfigState => {
  const state = useContext(RuntimeConfigContext);
  if (state === null) {
    throw new Error('useRuntimeConfig debe usarse dentro de <RuntimeConfigProvider>.');
  }
  return state;
};

export const useRuntimeConfig = (): RuntimeConfig => useRuntimeConfigState().config;

/**
 * Whether a module is on **for the company**, honouring its dependency.
 *
 * IMSS validation with the incapacity module off would render controls for a section
 * that is not reachable, so a dependent feature is off whenever its parent is.
 *
 * **No mira lo que haya decidido un área**, y no puede: este proveedor está por
 * encima de la sesión y no sabe quién mira. Para saber si un módulo está vivo
 * donde la persona trabaja —que es lo que decide qué se le ofrece— la pregunta es
 * `useAccess().featureOn`, que resuelve lo global contra el área en foco.
 */
export const useFeature = (feature: FeatureKey): boolean => {
  const config = useRuntimeConfig();
  if (config.features[feature] !== true) return false;
  if (feature === 'imss') return config.features.incapacidades === true;
  return true;
};

/** Whether a role holds a permission under the live matrix. */
export const usePermission = (role: Role | null, permission: Permission): boolean => {
  const config = useRuntimeConfig();
  if (role === null) return false;
  return can(config.rolePermissions, role, permission);
};

/**
 * Semver comparison for the soft version gate.
 *
 * Anything unparseable compares as "not older", so a malformed `minClientVersion`
 * cannot lock every client out of the app — the failure direction matters more here
 * than precision.
 */
export const isOlderThan = (current: string, minimum: string): boolean => {
  const parse = (value: string): number[] | null => {
    const parts = value.trim().split('.').map(Number);
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
    return parts;
  };

  const a = parse(current);
  const b = parse(minimum);
  if (a === null || b === null) return false;

  for (let i = 0; i < 3; i += 1) {
    const left = a[i] ?? 0;
    const right = b[i] ?? 0;
    if (left !== right) return left < right;
  }
  return false;
};
